import { List, Collapse, Button } from "antd";
import { useState } from "react";
import { CaretRightOutlined, EditOutlined } from "@ant-design/icons";
import UpdateDoc from "./components/UpdateDoc";
import EditOrderDetails from "./components/EditOrderDetails";

const styles = {
  leftSection: {
    display: "flex",
    flex: 2,
    flexDirection: "column",
    justifyContent: "space-between",
  },
  addressSection: {
    flex: 2,
    padding: "30px",
    backgroundColor: "#fff",
    borderRadius: 8,
    //display: "flex",
    //justifyContent: "space-between",
  },
  addressCol: {
    flex: 1,
  },
};
const FULL_ACCESS_ROLES = [
  "super_admin",
  "agnext_admin",
  "regulatory_body_admin",
  "company_admin",
];

const VIEW_ONLY_ROLES = [
  "csc_admin",
  "company_admin",
  "agnext_reviewer",
  "agnext_operations",
];

const canManageOrderDocuments = (userRole, orderStatus) => {
  if (FULL_ACCESS_ROLES.includes(userRole)) {
    return orderStatus !== "completed";
  }
  if (userRole === "company_user") {
    return orderStatus === "pending";
  }
  return false;
};

const PackageBlock = ({ data, commodity }) => {
  const [showDetails, setShowDetails] = useState(false);
  const { Panel } = Collapse;

  const { name, totalParametersForTest, testCategoryCounts } = data;

  const renderCategories = () =>
    Object.entries(testCategoryCounts).map(([categoryName, categoryData]) => (
      <Panel
        key={categoryName}
        header={
          <span style={{ fontWeight: "bold", color: "#0a8080" }}>
            {categoryName}
            <span
              style={{
                marginLeft: 8,
                background: "#e6f7ff",
                padding: "2px 8px",
                borderRadius: 12,
                fontSize: 12,
                color: "#096dd9",
              }}
            >
              {categoryData.count} tests
            </span>
          </span>
        }
        style={{
          marginBottom: 16,
          backgroundColor: "#eff3f7",
          border: "1px solid #E0E0E0",
          borderRadius: 4,
        }}
      >
        <List
          dataSource={categoryData.parameters}
          renderItem={(param) => (
            <List.Item>
              <List.Item.Meta title={param} />
            </List.Item>
          )}
        />
      </Panel>
    ));

  return (
    <div
      style={{
        border: "1px dashed #ddd",
        borderRadius: 8,
        padding: "20px 30px",
        background: "#fff",
        marginTop: 16,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <div>
          <p>
            <strong>Package:</strong> {data.name}
            <span
              style={{
                marginLeft: 10,
                background: "#f0f5ff",
                padding: "2px 10px",
                borderRadius: 12,
                fontSize: 12,
                color: "#2f54eb",
              }}
            >
              {data.totalParametersForTest} Total Tests
            </span>
          </p>

          <p style={{ marginTop: 4 }}>
            <strong>Commodity:</strong> {commodity}
          </p>
        </div>
        <p
          onClick={() => setShowDetails(!showDetails)}
          style={{ textDecoration: "underline", cursor: "pointer" }}
        >
          {showDetails ? "Hide Details" : "More Details"}
        </p>
      </div>

      {/* Categories */}
      {showDetails && (
        <Collapse
          bordered={false}
          expandIconPosition="right"
          expandIcon={({ isActive }) => (
            <CaretRightOutlined
              style={{ color: "#0a8080" }}
              rotate={isActive ? 90 : 0}
            />
          )}
          style={{ background: "#fff", marginTop: 12 }}
        >
          {renderCategories()}
        </Collapse>
      )}
    </div>
  );
};

const OrderDetails = ({ packages, orderDetails, user }) => {
  const documents = orderDetails?.uploadedDocuments || [];
  const projectConfig = orderDetails?.projectCodeId?.projectConfig || {};
  const metadataFields =orderDetails?.projectCodeId?.projectConfig?.metadataFields || {};

  const [isOpenEditModal, setIsOpenEditModal] = useState(false);

  const orderStatus = orderDetails?.status?.value;
  const canManageDocuments = canManageOrderDocuments(user?.role, orderStatus);

  const orderInfo = [
    { label: "Order ID", value: orderDetails?.orderId },
    metadataFields?.client?.required && {
      label: "Client",
      value: orderDetails?.company?.companyName || "-",
    },
    metadataFields?.agency?.required && {
      label: "Agency",
      value: orderDetails?.agency?.agencyName || "-",
    },
    {
      label: "Project Code",
      value: orderDetails?.projectCodeId?.projectCode || "-",
    },
    metadataFields?.variant?.required && {
      label: "Variant",
      value: orderDetails?.sampleVariant || "-",
    },
    metadataFields?.sampleType?.required && {
      label: "Sample Type",
      value: orderDetails?.sampleType || "-",
    },
    metadataFields?.destinationCountry?.required && {
      label: "Destination Country",
      value: orderDetails?.destinationCountry || "-",
    },
    metadataFields?.samplingType?.required && {
      label: "Sampling Type",
      value: orderDetails?.samplingType || "-",
    },
    orderDetails?.samplingType === "Code-wise Sampling" && {
      label: "Codewise Reason",
      value: orderDetails?.codewiseReason || "-",
    },
    metadataFields?.expectedConsignmentReadiness?.required && {
      label: "Expected Consignment Readiness",
      value: orderDetails?.expectedConsignmentReadiness
        ? new Date(
            orderDetails?.expectedConsignmentReadiness
          ).toLocaleDateString("en-GB")
        : "-",
    },
    metadataFields?.expectedShipmentDate?.required && {
      label: "Expected Shipment Date",
      value: orderDetails?.expectedShipmentDate
        ? new Date(orderDetails.expectedShipmentDate).toLocaleDateString(
            "en-GB"
          )
        : "-",
    },
    metadataFields?.batchId?.required && {
      label: "Batch ID",
      value: orderDetails?.batchId || "-",
    },
    metadataFields?.supplier?.required && {
      label: "Supplier",
      value: orderDetails?.supplier || "-",
    },
    metadataFields?.sector?.required && {
      label: "Sector",
      value: orderDetails?.sector || "-",
    },
    metadataFields?.quantity?.required && {
      label: "Lot Quantity",
      value: orderDetails?.quantity
        ? `${orderDetails.quantity} ${orderDetails?.quantityUnit || ""}`
        : "-",
    },
     metadataFields?.siteAddress?.required && {
      label: "Site Address",
      value: orderDetails?.siteAddress || "-",
    },
    metadataFields?.notes?.required && {
      label: "Additional Notes",
      value: orderDetails?.additionalNotes || "-",
    },
  ];

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "10px",
      }}
    >
      <div
        style={{
          padding: "20px 30px",
          border: "1px solid #E0E0E0",
          borderRadius: 8,
          backgroundColor: "#fff",
          display: "flex",
          flexDirection: "column",
          gap: "10px",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(20vw, 1fr))",
            gap: "10px",
            overflow: "auto",
          }}
        >
          {/* // Order Details */}
          <div style={{ flex: 1 }}>
            <p
              style={{
                fontWeight: "bold",
                fontSize: 16,
                paddingBottom: "5px",
                borderBottom: "1px solid #D9D9D9",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              Order Details
              {orderStatus === "orderCorrection" && (
                <Button
                  type="primary"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => setIsOpenEditModal(true)}
                >
                  Edit
                </Button>
              )}
            </p>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              {orderInfo?.filter(Boolean).map(({ label, value }) => (
                <p key={label} style={{ display: "flex", gap: "5px" }}>
                  <span style={{ fontWeight: "bold" }}>{label} :</span> {value}
                </p>
              ))}
              {/* edit order details modal */}
              {isOpenEditModal && (
                <EditOrderDetails
                  orderDetails={orderDetails}
                  visible={isOpenEditModal}
                  onClose={() => setIsOpenEditModal(false)}
                />
              )}

              {/* documents  */}
              {documents.length > 0 && (
                <div style={{ marginTop: 5 }}>
                  <p style={{ fontWeight: "bold", marginBottom: 4 }}>
                    Uploaded Documents :
                  </p>

                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 8,
                    }}
                  >
                    {documents.map((doc) => (
                      <div
                        key={doc._id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "12px 16px",
                          border: "1px solid #E2E8F0",
                          borderRadius: 10,
                          background: "#FFFFFF",
                          fontSize: 14,
                          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                          transition: "all 0.2s ease",
                        }}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              fontWeight: 600,
                              color: "#1E293B",
                              marginBottom: 4,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              fontSize: "15px",
                            }}
                            title={doc.documentName}
                          >
                            {doc.documentName}
                          </div>
                          <div
                            style={{
                              fontSize: 12,
                              color: "#64748B",
                              display: "flex",
                              flexDirection: "column",
                              gap: 2,
                            }}
                          >
                            <div style={{ display: "flex", gap: 16 }}>
                              <span
                                style={{
                                  background: "#F1F5F9",
                                  padding: "2px 6px",
                                  borderRadius: 4,
                                  fontSize: 11,
                                  fontWeight: 500,
                                  color: "#475569",
                                }}
                              >
                                {doc.documentType?.toUpperCase()}
                              </span>
                            </div>
                            <span
                              style={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                color: "#64748B",
                                fontStyle: "italic",
                              }}
                              title={doc.fileName}
                            >
                              {doc.fileName}
                            </span>
                          </div>
                        </div>

                        <a
                          href={doc.documentUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            fontWeight: 500,
                            color: "#0891B2",
                            textDecoration: "none",
                            padding: "6px 12px",
                            border: "1px solid #0891B2",
                            borderRadius: 6,
                            fontSize: 13,
                            marginLeft: 16,
                            flexShrink: 0,
                            background: "#FFFFFF",
                            transition: "all 0.2s ease",
                          }}
                        >
                          View Document
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div style={{ marginTop: 16, width: "100%" }}>
                {canManageDocuments &&
                  projectConfig?.projectDocuments?.length > 0 && (
                    <UpdateDoc
                      orderId={orderDetails._id}
                      uploadedDocuments={documents}
                      requiredDocuments={projectConfig?.projectDocuments}
                    />
                  )}
              </div>
            </div>
          </div>

          {/* // sampler details */}
          {user.role === "super_admin" && (
            <div style={{ flex: 1 }}>
              <p
                style={{
                  fontWeight: "bold",
                  fontSize: 16,
                  paddingBottom: "5px",
                  borderBottom: "1px solid #D9D9D9",
                }}
              >
                Sampler Details
              </p>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <p style={{ display: "flex", gap: "5px" }}>
                  <span style={{ fontWeight: "bold" }}>Name :</span>{" "}
                  {orderDetails.samplerName}
                </p>
                <p style={{ display: "flex", gap: "5px" }}>
                  <span style={{ fontWeight: "bold" }}>Contact :</span>{" "}
                  {orderDetails.samplerContact}
                </p>
                <p
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "5px",
                  }}
                >
                  <span style={{ fontWeight: "bold" }}>Address :</span>{" "}
                  {orderDetails.samplerAddress}
                </p>
              </div>
            </div>
          )}

          {/* // lab response  */}
          <div style={{ flex: 1 }}>
            <p
              style={{
                fontWeight: "bold",
                fontSize: 16,
                paddingBottom: "5px",
                borderBottom: "1px solid #D9D9D9",
              }}
            >
              Pickup Address
            </p>
            <p>{orderDetails.samplerAddress}</p>
          </div>
        </div>
        <div
          style={{
            marginTop: "20px",
            display: "flex",
            justifyContent: "space-between",
            overflow: "auto",
          }}
        >
          <div style={styles.addressCol}>
            <p
              style={{
                fontWeight: "bold",
                fontSize: 16,
                paddingBottom: "5px",
                borderBottom: "1px solid #D9D9D9",
              }}
            >
              Pickup Request Date
            </p>
            <p>{orderDetails.outForPickupDate}</p>
          </div>
        </div>
      </div>
      <div
        style={{
          padding: "20px 30px",
          border: "1px solid #E0E0E0",
          borderRadius: 8,
          backgroundColor: "#fff",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <p
          style={{
            fontWeight: "bold",
            fontSize: 16,
            paddingBottom: "5px",
            borderBottom: "1px solid #D9D9D9",
          }}
        >
          Order Inclusions
        </p>
        {packages.map((packageDetails) => (
          <PackageBlock
            key={packageDetails._id}
            data={packageDetails}
            commodity={orderDetails.commodity}
          />
        ))}
      </div>
    </div>
  );
};

export default OrderDetails;
